import { nanoid } from 'nanoid';
import { NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import type { ShowCrossTabNotification } from 'modules/EventsManager';
import { PageTypes } from 'utils/pageTypes';
import type {
  HandleAutoloadNextCaseParams,
  MaybeAutoloadNextCaseResult,
} from 'hooks/useWorklistAutoLoad';
import type { ReportExitAction } from 'domains/reporter/RichTextEditor/analytics/performance';

/**
 * Shared "report exit action" side effects — the things that run when the user finishes with a
 * report or addendum (draft / sign / prelim / discard / addendum-submit). Pure functions that
 * receive their live callbacks as inputs, consistent with useAutoSaveReport.
 */

type MaybeAutoloadNextCaseFn = (
  isEnabled: boolean,
  defaultToastMsg: string,
  params: HandleAutoloadNextCaseParams
) => Promise<MaybeAutoloadNextCaseResult>;

/**
 * Autoload the next case (which also handles patient-jacket + current-case clearing internally) and
 * broadcast the resulting exit toast to the worklist tab.
 */
export const runAutoloadNextCaseWithBroadcast = async ({
  maybeAutoloadNextCase,
  isWorklistAutoLoadEnabled,
  defaultToastMsg,
  autoloadParams,
}: {
  maybeAutoloadNextCase: MaybeAutoloadNextCaseFn;
  isWorklistAutoLoadEnabled: boolean;
  defaultToastMsg: string;
  autoloadParams: HandleAutoloadNextCaseParams;
}): Promise<void> => {
  const result = await maybeAutoloadNextCase(
    isWorklistAutoLoadEnabled,
    defaultToastMsg,
    autoloadParams
  );

  sendCrossTabEvent(NameSpaces.CROSS_TAB_NOTIFICATION, {
    type: 'showCrossTabNotification',
    payload: {
      toastKey: nanoid(),
      targetPageTypes: [PageTypes.WORKLIST],
      message: result.toastMsg,
      ...result.toastOptions,
    },
  } as ShowCrossTabNotification);
};

/** Emit the report-exit "end" telemetry marker for a completed exit action. */
export const emitReportExitActionEnd = (
  measureReportExitActionEnd: (metadata: { exitAction: ReportExitAction }) => void,
  exitAction: ReportExitAction
): void => {
  measureReportExitActionEnd({ exitAction });
};
